import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { useLocation, useNavigate } from '@remix-run/react';
import { ExitToPreviousState, RequiredContext } from './types';
import { useSideOverContext } from '../UI';
import {
  appendQuarterlyProgressReportReturnTo,
  getSafeQuarterlyProgressReportReturnTo,
  isQuarterlyProgressReportRelativePath,
} from '../QuarterlyProgressReport';

export type SideoverNavigationContainerTo<E> =
  | (({clientID, entity}: { clientID: string; entity: E }) => string)
  | string;

export function SideoverNavigationContainer<E extends { id: string }>({
  children,
  to,
  back,
  clientContext
}: { children: ({ householdID }: {
    householdID: string;
    navigate: (entity: E) => void;
    clientID: string;
  }) => PropsWithChildren['children'];
  to: SideoverNavigationContainerTo<E>;
  back: (({clientID}: { clientID: string }) => string) | string;
  clientContext: ReturnType<typeof createContext<RequiredContext | null>>
}): ReturnType<FC> {
  const context = useContext(clientContext);
  if (!context) {
    throw new Error('Unable to use provided context.');
  }
  const {householdID, clientID} = context;
  const navigate = useNavigate();
  const location = useLocation();
  const {exitRoute: fallbackExitRoute} = useSideOverContext();
  const quarterlyProgressReportReturnTo = getSafeQuarterlyProgressReportReturnTo({
    clientID,
    returnTo: new URLSearchParams(location.search).get('returnTo'),
  });
  const withQuarterlyProgressReportReturnTo = (path: string) => {
    if (!quarterlyProgressReportReturnTo || path === quarterlyProgressReportReturnTo) {
      return path;
    }

    if (!isQuarterlyProgressReportRelativePath(path)) {
      return path;
    }

    return appendQuarterlyProgressReportReturnTo(path, quarterlyProgressReportReturnTo);
  };

  return (
    <>
      {children({
        householdID,
        navigate: (entity) => {
          const backPath = (typeof back === 'string' ? back : back({clientID})) || fallbackExitRoute;

          navigate(withQuarterlyProgressReportReturnTo(typeof to === 'string' ? to : to({clientID, entity})), {
            state: {
              exitRoute: backPath ? withQuarterlyProgressReportReturnTo(backPath) : undefined,
            } as ExitToPreviousState,
          });
        },
        clientID,
      })}
    </>
  );
}
